"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useAtom, type WritableAtom } from "jotai";
import { RESET } from "jotai/utils";
import { useRouter } from "@/i18n/navigation";
import { useToast } from "@/hooks/useToast";
import { hotelCheckoutPayloadAtom } from "@/stores/hotel-checkout";
import { rentalCheckoutPayloadAtom } from "@/stores/rental-checkout";
import { CheckoutPayload } from "./booking.checkout.helpers";
import { tourActCheckoutPayloadAtom } from "@/stores/tour-act-checkout";
import { spaCheckoutPayloadAtom } from "@/stores/spa-checkout";

export type CheckoutServiceConfig<T> = {
  basePath: string;
  idField: keyof T & string;
  atom: WritableAtom<T | null, [T | null | typeof RESET], void>;
};

export const hotelCheckoutConfig: CheckoutServiceConfig<CheckoutPayload> = {
  basePath: "/hotels/checkout",
  idField: "hotelId",
  atom: hotelCheckoutPayloadAtom,
};

export const rentalCheckoutConfig: CheckoutServiceConfig<CheckoutPayload> = {
  basePath: "/rentals/checkout",
  idField: "rentalId",
  atom: rentalCheckoutPayloadAtom,
};

export const tourActCheckoutConfig: CheckoutServiceConfig<CheckoutPayload> = {
  basePath: "/tour-act/checkout",
  idField: "activityId",
  atom: tourActCheckoutPayloadAtom,
};

export const spaMsgCheckoutConfig: CheckoutServiceConfig<CheckoutPayload> = {
  basePath: "/spa-msg/checkout",
  idField: "spaId",
  atom: spaCheckoutPayloadAtom,
};

export function useCheckoutSessionGuard<
  T extends { start: string; end: string },
>(entityId: string, config: CheckoutServiceConfig<T>) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { toast } = useToast();

  const [checkoutPayload, setCheckoutPayload] = useAtom(config.atom);
  const [isHydrated, setIsHydrated] = useState(false);
  const hasHandledInvalidStateRef = useRef(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const fallbackUrl = useMemo(() => {
    const currentQueryString = searchParams.toString();
    return `${config.basePath}/${entityId}${currentQueryString ? `?${currentQueryString}` : ""}`;
  }, [config.basePath, entityId, searchParams]);

  const clearCheckoutPayload = useCallback(() => {
    setCheckoutPayload(RESET);
  }, [setCheckoutPayload]);

  /**
   * Handle invalid checkout session by clearing the checkout payload,
   * showing a toast message, and redirecting to the fallback URL.
   */
  const handleInvalidCheckoutSession = useCallback(() => {
    hasHandledInvalidStateRef.current = true;
    clearCheckoutPayload();
    router.replace(fallbackUrl);
  }, [clearCheckoutPayload, fallbackUrl, router]);

  useEffect(() => {
    if (!isHydrated || hasHandledInvalidStateRef.current) {
      return;
    }

    if (!checkoutPayload) {
      handleInvalidCheckoutSession();
      return;
    }

    const startParam = searchParams.get("start");
    const endParam = searchParams.get("end");
    const hasEntityMismatch = checkoutPayload[config.idField] !== entityId;
    const hasStartMismatch = Boolean(
      startParam && checkoutPayload.start !== startParam,
    );
    const hasEndMismatch = Boolean(
      endParam && checkoutPayload.end !== endParam,
    );

    if (hasEntityMismatch || hasStartMismatch || hasEndMismatch) {
      handleInvalidCheckoutSession();
    }
  }, [
    checkoutPayload,
    config.idField,
    entityId,
    handleInvalidCheckoutSession,
    isHydrated,
    searchParams,
  ]);

  return {
    checkoutPayload,
    isHydrated,
    clearCheckoutPayload,
  };
}
